// src/ManageRooms.js - AGGIORNATO (Senza Geofence e Turni Base)

import React, { useState, useEffect, useCallback } from 'react';
import { db } from './firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { RoomModal } from './RoomModal';
import { toast } from 'react-toastify';
import { useUser } from './UserContext';
import { logAction } from './loggingService';

function ManageRooms() {
    const { userProfile } = useUser(); 
    
    const permissionLevel = userProfile?.permissions?.rooms || 'none';
    const canEdit = permissionLevel === 'completo' || permissionLevel === 'semi-completo';
    const canDelete = permissionLevel === 'completo';
    const [rooms, setRooms] = useState([]);
    const [allEquipment, setAllEquipment] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [businessHours, setBusinessHours] = useState({});
    const [selectedDays, setSelectedDays] = useState([1]);

    const daysOfWeek = [
        { label: 'LUN', value: 1, full: 'Lunedì' }, { label: 'MAR', value: 2, full: 'Martedì' },
        { label: 'MER', value: 3, full: 'Mercoledì' }, { label: 'GIO', value: 4, full: 'Giovedì' },
        { label: 'VEN', value: 5, full: 'Venerdì' }, { label: 'SAB', value: 6, full: 'Sabato' }
    ];

    const fetchData = useCallback(async () => {
        if (!userProfile || !userProfile.centerId) {
            setRooms([]);
            setAllEquipment([]);
            return; 
        }
        const centerId = userProfile.centerId;

        // Query ridotte: carichiamo solo stanze, macchinari e orari
        const roomsSnapshot = await getDocs(collection(db, 'centers', centerId, 'rooms'));
        const equipmentSnapshot = await getDocs(collection(db, 'centers', centerId, 'equipment'));
        const hoursDocRef = doc(db, 'centers', centerId, 'settings', 'businessHours');
        
        const hoursDocSnap = await getDoc(hoursDocRef);

        const equipmentList = equipmentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllEquipment(equipmentList);

        const roomsList = roomsSnapshot.docs.map(doc => {
             const data = doc.data();
             const equipmentNames = (data.equipmentIds || [])
                 .map(id => equipmentList.find(e => e.id === id)?.name)
                 .filter(Boolean);
             return { id: doc.id, ...doc.data(), equipmentNames };
        }).sort((a, b) => (a.number || 0) - (b.number || 0));
        setRooms(roomsList);

        if (hoursDocSnap.exists()) {
            setBusinessHours(hoursDocSnap.data());
        } else {
             // Default se non esiste
             setBusinessHours({
                 1: { open: '08:00', close: '20:00' }, 2: { open: '08:00', close: '20:00' },
                 3: { open: '08:00', close: '20:00' }, 4: { open: '08:00', close: '20:00' },
                 5: { open: '08:00', close: '20:00' }, 6: { open: '09:00', close: '13:00' },
             });
        }
    }, [userProfile]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDayToggle = (dayValue) => {
        setSelectedDays(prev =>
            prev.includes(dayValue)
                ? prev.filter(d => d !== dayValue)
                : [...prev, dayValue]
        );
    };

    const handleTimeChange = (type, value) => {
        setBusinessHours(prev => {
            const newHours = { ...prev };
            selectedDays.forEach(day => {
                newHours[day] = { ...newHours[day], [type]: value };
            });
            return newHours;
        });
    };

    const handleSaveHours = async () => {
        try {
            await setDoc(doc(db, 'centers', userProfile.centerId, 'settings', 'businessHours'), businessHours);
            await logAction(userProfile, "ha modificato gli orari di apertura/chiusura.");
            toast.success("Orari salvati!");
        } catch (error) {
            toast.error("Errore nel salvataggio degli orari.");
            console.error("Errore salvataggio orari: ", error);
        }
    };

    const handleSaveRoom = async (roomData) => {
        const dataToSave = {
            name: roomData.name,
            number: roomData.number,
            capacity: roomData.capacity, // 🎯 NUOVO: Salva la capienza in Firebase
            color: roomData.color || null, // 🆕 Colore personalizzato colonna Agenda (opzionale)
            equipmentIds: roomData.equipmentIds,
            centerId: userProfile.centerId,
        };
        if (roomData.id) {
            await updateDoc(doc(db, 'centers', userProfile.centerId, 'rooms', roomData.id), dataToSave);
            await logAction(userProfile, `ha modificato la stanza: ${roomData.name}`);
        } else {
            await addDoc(collection(db, 'centers', userProfile.centerId, 'rooms'), dataToSave);
            await logAction(userProfile, `ha creato la stanza: ${roomData.name}`);
        }
        setIsModalOpen(false);
        fetchData();
    };
    
    const handleDuplicateRoom = async (roomToDuplicate) => {
        const newName = window.prompt(
            'Inserisci il nome per la nuova stanza:',
            `${roomToDuplicate.name} (Copia)`
        );
        if (!newName || newName.trim() === '') return; 

        const nextNumber = rooms.length > 0 ? Math.max(...rooms.map(r => r.number)) + 1 : 1;

        const newRoomData = {
            name: newName.trim(),
            number: nextNumber,
            capacity: roomToDuplicate.capacity || 1, // 🎯 NUOVO: Duplica anche la capienza
            color: roomToDuplicate.color || null, // 🆕 Duplica anche il colore personalizzato
            equipmentIds: roomToDuplicate.equipmentIds || [],
            centerId: userProfile.centerId,
        };

        try {
            await addDoc(collection(db, 'centers', userProfile.centerId, 'rooms'), newRoomData);
            await logAction(userProfile, `ha duplicato la stanza "${roomToDuplicate.name}" in "${newRoomData.name}"`);
            fetchData();
        } catch (error) {
            console.error("Errore nella duplicazione della stanza: ", error);
        }
    };

    const handleDeleteRoom = async (roomId) => {
        const roomToDelete = rooms.find(r => r.id === roomId);
        if (window.confirm("Sei sicuro...?")) {
            await deleteDoc(doc(db, 'centers', userProfile.centerId, 'rooms', roomId));
            if (roomToDelete) {
                await logAction(userProfile, `ha eliminato la stanza: ${roomToDelete.name}`);
            }
            fetchData();
        }
    };

    const handleEditClick = (room) => { setEditingRoom(room); setIsModalOpen(true); };
    const handleNewClick = () => { setEditingRoom(null); setIsModalOpen(true); };

    return (
        <>
            {isModalOpen && (
                <RoomModal
                    roomData={editingRoom}
                    allEquipment={allEquipment}
                    onSave={handleSaveRoom}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
            
            <div className="management-container">
                <div className="management-header">
                    {/* Sezione Orari */}
                    {canEdit && (
                        <div className="hours-settings-container">
                            <div className="day-selector">
                                {daysOfWeek.map(day => (
                                    <div key={day.value} className="day-checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            id={`day-select-${day.value}`}
                                            checked={selectedDays.includes(day.value)}
                                            onChange={() => handleDayToggle(day.value)}
                                        />
                                        <label htmlFor={`day-select-${day.value}`} title={day.full}>{day.label}</label>
                                    </div>
                                ))}
                            </div>
                            <div className="time-inputs">
                                <input
                                    type="time"
                                    value={selectedDays.every(d => businessHours[d]?.open === businessHours[selectedDays[0]]?.open) ? businessHours[selectedDays[0]]?.open || '' : ''}
                                    onChange={e => handleTimeChange('open', e.target.value)}
                                />
                                <span>-</span>
                                <input
                                    type="time"
                                    value={selectedDays.every(d => businessHours[d]?.close === businessHours[selectedDays[0]]?.close) ? businessHours[selectedDays[0]]?.close || '' : ''}
                                    onChange={e => handleTimeChange('close', e.target.value)}
                                />
                            </div>
                            <button onClick={handleSaveHours} className="save-hours-btn">Salva Orari</button>
                        </div>
                     )}
                    {/* Pulsante Aggiungi Stanza */}
                    {canEdit && (
                        <button onClick={handleNewClick} className="add-new-button">+ Aggiungi Stanza</button>
                    )}
                </div>

                {/* Riepilogo Orari */}
                <div className="hours-summary">
                    <h4>Riepilogo Orari di Apertura</h4>
                    <div className="hours-summary-grid">
                        {daysOfWeek.map(day => (
                            <div key={day.value} className="summary-day-card">
                                <div className="summary-day-label">{day.full}</div>
                                <div className="summary-day-time">
                                    {businessHours[day.value] ? `${businessHours[day.value].open} - ${businessHours[day.value].close}` : 'Chiuso'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Lista Stanze */}
                <div className="management-list" style={{ marginTop: '30px' }}>
                    {rooms.map(room => (
                        <div key={room.id} className="management-card">
                            <div className="list-item-content">
                                {/* 🎯 NUOVO: Aggiunta l'indicazione della capienza visiva */}
                                <span className="list-item-main-text">
                                    {room.color && <span title="Colore personalizzato" style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: room.color, marginRight: '8px', border: '1px solid rgba(0,0,0,0.15)', verticalAlign: 'middle' }}></span>}
                                    {room.name} (N. {room.number}) <span style={{ color: '#64748b', fontSize: '0.85em', marginLeft: '5px' }}>• Capienza: {room.capacity || 1} {room.capacity > 1 ? 'posti' : 'posto'}</span>
                                </span>
                                <div className="equipment-tags-container">
                                    {room.equipmentNames.map(name => <span key={name} className="equipment-tag">{name}</span>)}
                                </div>
                            </div>
                            
                            <div className="list-item-actions">
                                {canEdit && <button onClick={() => handleDuplicateRoom(room)} className="duplicate-button">Duplica</button>}
                                {canEdit && <button onClick={() => handleEditClick(room)} className="info-button">Modifica</button>}
                                {canDelete && <button onClick={() => handleDeleteRoom(room.id)} className="delete-button">Elimina</button>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default ManageRooms;